import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  computed,
  forwardRef,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Intervalle de dates (mode « range ») : bornes au format ISO `YYYY-MM-DD`. */
export interface DateRange {
  from: string | null;
  to: string | null;
}

/** Mode de sélection du datepicker. */
export type DatepickerMode = 'single' | 'range';

/**
 * Valeur exposée : une date ISO (`single`) ou un intervalle (`range`).
 * `null` (ou `{ from: null, to: null }`) = aucune sélection.
 */
export type DatepickerValue = string | DateRange | null;

const MONTHS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const pad = (n: number): string => String(n).padStart(2, '0');

/** Date → ISO local `YYYY-MM-DD` (sans décalage de fuseau). */
function toIso(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** ISO `YYYY-MM-DD` → Date locale à minuit, ou `null` si invalide. */
function fromIso(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** Nouvelle Date décalée de `n` jours (arithmétique locale sûre). */
function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** ISO → affichage français `JJ/MM/AAAA` (chaîne vide si absent). */
function formatFr(value: string | null): string {
  const d = fromIso(value);
  return d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : '';
}

/** Cellule « jour » de la grille du calendrier. */
interface DayCell {
  iso: string;
  day: number;
  inMonth: boolean;
  disabled: boolean;
}

/**
 * Sélecteur de date du kit IDRA — calendrier flottant, mode date unique ou
 * intervalle, intégrable aux formulaires (ControlValueAccessor).
 *
 * Usage (hors formulaire) :
 *   <idra-datepicker [(value)]="jour" />
 *   <idra-datepicker mode="range" [value]="plage" (valueChange)="onPlage($event)" [max]="aujourdhui" />
 *
 * Usage (formulaire) :
 *   <idra-datepicker formControlName="date" />
 *
 * La valeur émise est une chaîne ISO `YYYY-MM-DD` en mode `single`, un objet
 * `DateRange` en mode `range`. `min`/`max` (ISO) bornent les dates cliquables.
 */
@Component({
  selector: 'idra-datepicker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => IdraDatepicker), multi: true },
  ],
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
  styles: `
    :host {
      display: block;
      position: relative;
    }
    .trigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      text-align: left;
      cursor: pointer;
    }
    .trigger .placeholder {
      color: var(--idra-text-muted);
    }
    .trigger .cal {
      flex: none;
      font-size: 14px;
      color: var(--idra-text-muted);
    }
    .panel {
      position: absolute;
      z-index: 20;
      top: calc(100% + 6px);
      left: 0;
      min-width: 260px;
      padding: 10px;
      background: var(--idra-surface-overlay);
      border: 1px solid var(--idra-border);
      border-radius: var(--idra-radius-md);
      box-shadow: var(--idra-shadow-lg);
    }
    .head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 4px;
      margin-bottom: 8px;
    }
    .head .title {
      font-size: 14px;
      font-weight: 700;
      color: var(--idra-text);
    }
    .nav {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: 0;
      border-radius: var(--idra-radius-xs);
      background: transparent;
      color: var(--idra-text-secondary);
      cursor: pointer;
    }
    .nav:hover {
      background: var(--idra-surface-hover);
      color: var(--idra-text);
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 2px;
    }
    .wd {
      padding: 4px 0;
      text-align: center;
      font-size: 11px;
      font-weight: 700;
      color: var(--idra-text-muted);
    }
    .day {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 32px;
      border: 0;
      border-radius: var(--idra-radius-xs);
      background: transparent;
      color: var(--idra-text);
      font: inherit;
      font-size: 13px;
      cursor: pointer;
    }
    .day:hover:not(:disabled) {
      background: var(--idra-surface-hover);
    }
    .day.out {
      color: var(--idra-text-muted);
    }
    .day.today {
      font-weight: 700;
      box-shadow: inset 0 0 0 1px var(--idra-border-strong);
    }
    .day.in-range {
      background: var(--idra-primary-soft);
      border-radius: 0;
    }
    .day.selected {
      background: var(--idra-primary);
      color: var(--idra-primary-contrast);
      font-weight: 700;
    }
    .day.range-start {
      border-top-left-radius: var(--idra-radius-xs);
      border-bottom-left-radius: var(--idra-radius-xs);
    }
    .day.range-end {
      border-top-right-radius: var(--idra-radius-xs);
      border-bottom-right-radius: var(--idra-radius-xs);
    }
    .day:disabled {
      color: var(--idra-text-muted);
      opacity: 0.5;
      cursor: not-allowed;
    }
    .day:focus-visible {
      outline: none;
      box-shadow: var(--idra-focus-ring);
    }
    .foot {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid var(--idra-border);
    }
    .link {
      padding: 4px 6px;
      border: 0;
      background: none;
      font-size: 13px;
      color: var(--idra-primary);
      cursor: pointer;
    }
    .link:hover {
      text-decoration: underline;
    }
    .link.muted {
      color: var(--idra-text-muted);
    }
  `,
  template: `
    <button
      type="button"
      class="idra-input trigger"
      [id]="inputId() ?? null"
      [attr.aria-label]="ariaLabel() ?? null"
      [attr.aria-haspopup]="'dialog'"
      [attr.aria-expanded]="open()"
      [disabled]="disabled()"
      (click)="toggle()"
      (keydown)="onTriggerKeydown($event)"
      (blur)="onTouched()"
    >
      @if (triggerLabel()) {
        <span class="label">{{ triggerLabel() }}</span>
      } @else {
        <span class="placeholder">{{ placeholder() }}</span>
      }
      <i class="cal pi pi-calendar" aria-hidden="true"></i>
    </button>

    @if (open()) {
      <div #panel class="panel" role="dialog" [attr.aria-label]="ariaLabel() ?? 'Calendrier'">
        <div class="head">
          <button type="button" class="nav" aria-label="Mois précédent" (click)="shiftView(-1)">
            <i class="pi pi-chevron-left" aria-hidden="true"></i>
          </button>
          <span class="title" aria-live="polite">{{ monthLabel() }}</span>
          <button type="button" class="nav" aria-label="Mois suivant" (click)="shiftView(1)">
            <i class="pi pi-chevron-right" aria-hidden="true"></i>
          </button>
        </div>

        <div class="grid" role="grid" (keydown)="onGridKeydown($event)">
          @for (wd of weekdays; track wd) {
            <span class="wd" role="columnheader">{{ wd }}</span>
          }
          @for (week of weeks(); track $index) {
            @for (cell of week; track cell.iso) {
              <button
                type="button"
                class="day"
                role="gridcell"
                [attr.data-iso]="cell.iso"
                [class.out]="!cell.inMonth"
                [class.today]="isToday(cell.iso)"
                [class.selected]="isSelected(cell.iso)"
                [class.in-range]="isInRange(cell.iso)"
                [class.range-start]="isRangeStart(cell.iso)"
                [class.range-end]="isRangeEnd(cell.iso)"
                [attr.aria-selected]="isSelected(cell.iso)"
                [attr.aria-current]="isToday(cell.iso) ? 'date' : null"
                [attr.tabindex]="cell.iso === focused() ? 0 : -1"
                [disabled]="cell.disabled"
                (click)="pick(cell.iso)"
                (mouseenter)="hovered.set(cell.iso)"
                (mouseleave)="hovered.set(null)"
              >
                {{ cell.day }}
              </button>
            }
          }
        </div>

        <div class="foot">
          <button type="button" class="link" (click)="goToday()">Aujourd'hui</button>
          @if (triggerLabel()) {
            <button type="button" class="link muted" (click)="clear()">Effacer</button>
          }
        </div>
      </div>
    }
  `,
})
export class IdraDatepicker implements ControlValueAccessor {
  private host = inject(ElementRef<HTMLElement>);

  readonly mode = input<DatepickerMode>('single');
  readonly placeholder = input('Sélectionner une date…');
  readonly ariaLabel = input<string | undefined>(undefined);
  readonly inputId = input<string | undefined>(undefined);
  /** Bornes ISO `YYYY-MM-DD` : dates hors intervalle non cliquables. */
  readonly min = input<string | undefined>(undefined);
  readonly max = input<string | undefined>(undefined);

  readonly value = model<DatepickerValue>(null);

  protected readonly weekdays = WEEKDAYS;
  protected readonly disabled = signal(false);
  protected readonly open = signal(false);
  protected readonly hovered = signal<string | null>(null);
  protected readonly focused = signal<string | null>(null);

  private readonly todayIso = toIso(new Date());
  protected readonly viewYear = signal(new Date().getFullYear());
  protected readonly viewMonth = signal(new Date().getMonth());

  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  /** Date unique courante (mode `single`), sinon `null`. */
  protected readonly single = computed(() => {
    const v = this.value();
    return this.mode() === 'single' && typeof v === 'string' ? v : null;
  });

  /** Intervalle courant normalisé (mode `range`). */
  protected readonly range = computed<DateRange>(() => {
    const v = this.value();
    if (this.mode() === 'range' && v && typeof v === 'object') {
      return { from: v.from ?? null, to: v.to ?? null };
    }
    return { from: null, to: null };
  });

  protected readonly monthLabel = computed(() => `${MONTHS[this.viewMonth()]} ${this.viewYear()}`);

  protected readonly triggerLabel = computed(() => {
    if (this.mode() === 'single') return formatFr(this.single());
    const r = this.range();
    if (!r.from && !r.to) return '';
    return `${formatFr(r.from) || '…'} – ${formatFr(r.to) || '…'}`;
  });

  /** Grille de 6 semaines × 7 jours du mois affiché (semaine débutant lundi). */
  protected readonly weeks = computed<DayCell[][]>(() => {
    const y = this.viewYear();
    const m = this.viewMonth();
    const first = new Date(y, m, 1);
    const offset = (first.getDay() + 6) % 7; // jours écoulés depuis lundi
    let cursor = addDays(first, -offset);
    const weeks: DayCell[][] = [];
    for (let w = 0; w < 6; w++) {
      const row: DayCell[] = [];
      for (let i = 0; i < 7; i++) {
        const iso = toIso(cursor);
        row.push({
          iso,
          day: cursor.getDate(),
          inMonth: cursor.getMonth() === m,
          disabled: this.isDisabled(iso),
        });
        cursor = addDays(cursor, 1);
      }
      weeks.push(row);
    }
    return weeks;
  });

  protected onChange: (value: DatepickerValue) => void = () => {};
  protected onTouched: () => void = () => {};

  protected isToday(iso: string): boolean {
    return iso === this.todayIso;
  }

  protected isDisabled(iso: string): boolean {
    const min = this.min();
    const max = this.max();
    if (min && iso < min) return true;
    if (max && iso > max) return true;
    return false;
  }

  protected isSelected(iso: string): boolean {
    if (this.mode() === 'single') return this.single() === iso;
    const r = this.range();
    return r.from === iso || r.to === iso;
  }

  protected isRangeStart(iso: string): boolean {
    return this.mode() === 'range' && this.range().from === iso && iso !== this.range().to;
  }

  protected isRangeEnd(iso: string): boolean {
    const r = this.range();
    return this.mode() === 'range' && r.to === iso && iso !== r.from;
  }

  /** Jour strictement entre les bornes (aperçu au survol si la fin manque). */
  protected isInRange(iso: string): boolean {
    if (this.mode() !== 'range') return false;
    const r = this.range();
    if (!r.from) return false;
    const end = r.to ?? this.hovered();
    if (!end) return false;
    const lo = r.from <= end ? r.from : end;
    const hi = r.from <= end ? end : r.from;
    return iso > lo && iso < hi;
  }

  protected toggle(): void {
    this.open() ? this.close() : this.openPanel();
  }

  private openPanel(): void {
    const anchor = this.mode() === 'single' ? this.single() : this.range().from;
    const d = fromIso(anchor) ?? new Date();
    this.viewYear.set(d.getFullYear());
    this.viewMonth.set(d.getMonth());
    this.focused.set(anchor ?? this.todayIso);
    this.open.set(true);
    queueMicrotask(() => this.focusFocusedCell());
  }

  private close(): void {
    this.open.set(false);
    this.hovered.set(null);
  }

  protected shiftView(deltaMonths: number): void {
    const d = new Date(this.viewYear(), this.viewMonth() + deltaMonths, 1);
    this.viewYear.set(d.getFullYear());
    this.viewMonth.set(d.getMonth());
  }

  protected goToday(): void {
    const d = new Date();
    this.viewYear.set(d.getFullYear());
    this.viewMonth.set(d.getMonth());
    this.focused.set(this.todayIso);
    queueMicrotask(() => this.focusFocusedCell());
  }

  protected pick(iso: string): void {
    if (this.isDisabled(iso)) return;

    if (this.mode() === 'single') {
      this.value.set(iso);
      this.onChange(iso);
      this.close();
      return;
    }

    const r = this.range();
    let next: DateRange;
    if (!r.from || r.to) {
      // Aucune borne, ou intervalle complet : on démarre une nouvelle plage.
      next = { from: iso, to: null };
    } else if (iso < r.from) {
      // Deuxième clic avant le début : on redémarre au nouveau début.
      next = { from: iso, to: null };
    } else {
      next = { from: r.from, to: iso };
    }
    this.value.set(next);
    this.onChange(next);
    if (next.to) this.close();
  }

  protected clear(): void {
    const empty: DatepickerValue = this.mode() === 'range' ? { from: null, to: null } : null;
    this.value.set(empty);
    this.onChange(empty);
  }

  protected onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!this.open()) this.openPanel();
    } else if (event.key === 'Escape') {
      this.close();
    }
  }

  protected onGridKeydown(event: KeyboardEvent): void {
    const base = fromIso(this.focused()) ?? new Date();
    switch (event.key) {
      case 'ArrowLeft':
        this.moveFocus(addDays(base, -1));
        break;
      case 'ArrowRight':
        this.moveFocus(addDays(base, 1));
        break;
      case 'ArrowUp':
        this.moveFocus(addDays(base, -7));
        break;
      case 'ArrowDown':
        this.moveFocus(addDays(base, 7));
        break;
      case 'PageUp':
        this.shiftView(-1);
        break;
      case 'PageDown':
        this.shiftView(1);
        break;
      case 'Enter':
      case ' ': {
        const iso = this.focused();
        if (iso && !this.isDisabled(iso)) this.pick(iso);
        break;
      }
      case 'Escape':
        this.close();
        return;
      case 'Tab':
        this.close();
        return; // laisse le focus sortir naturellement
      default:
        return;
    }
    event.preventDefault();
  }

  private moveFocus(d: Date): void {
    if (d.getFullYear() !== this.viewYear() || d.getMonth() !== this.viewMonth()) {
      this.viewYear.set(d.getFullYear());
      this.viewMonth.set(d.getMonth());
    }
    this.focused.set(toIso(d));
    queueMicrotask(() => this.focusFocusedCell());
  }

  private focusFocusedCell(): void {
    const iso = this.focused();
    const el = this.panel()?.nativeElement.querySelector<HTMLElement>(`[data-iso="${iso}"]`);
    el?.focus();
  }

  protected onDocumentClick(event: Event): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  writeValue(value: DatepickerValue): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: DatepickerValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
